import { TOAST_WITH_ACTION_DURATION } from "@/constants/ui"
import {
  Collection,
  CollectionImportType,
  CollectionRef,
  CollectionTrigger,
} from "@/types/collection"
import { PlaceRef } from "@/types/entity"
import { Inspiration } from "@/types/inspiration"
import { FavoritePlace, Place } from "@/types/place"
import { SavedTab, SavedType, SavedView } from "@/types/saved"
import { TripRef } from "@/types/trip"
import { UserRef } from "@/types/user"
import { BoundingBox } from "@/types/util"
import { createAsyncThunk } from "@reduxjs/toolkit"
import pluralize from "pluralize"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { buildClientAxios } from "@/lib/client-axios"
import { placePlaceholderToMapMarker } from "@/lib/map-marker-utils"
import { clearPendingSave, getPendingSave } from "@/lib/pending-save"
import { SavedPlaceApi } from "@/lib/saved-place-api"
import { isSerializedClientError } from "@/lib/storage/api/base-query"
import { collectionsApi } from "@/lib/storage/api/collections-api"
import { savedInspirationsApi } from "@/lib/storage/api/saved-inspirations-api"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import savedSlice, {
  selectCollectionDialogIsLoading,
  selectCollectionDialogIsPrivate,
  selectCollectionDialogName,
  selectCollectionTrigger,
  selectCurrentTab,
  selectCurrentView,
  selectEditingCollection,
  selectMapMarkers,
  selectSaveDialogPlace,
  selectSelectedType,
  setCollectionDialogError,
  setCollectionDialogIsLoading,
  setCollectionDialogIsPrivate,
  setCollectionDialogName,
  setCollectionDialogOpen,
  setCurrentTab,
  setCurrentView,
  setEditingCollection,
  setLastUsedCollection,
  setLoading,
  setMapMarkers,
  setPendingCollectionImport,
  setPlaceSearchRequestId,
  setPlaceSearchResults,
  setSaveDialogOpen,
  setSaveDialogPlace,
  setSaveDialogPlaceRefs,
  setSearchString,
  setSelectedType,
} from "@/lib/storage/saved-slice"
import userSlice, { selectCurrentUserRef } from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import { present } from "@/lib/utils/presence"
import IconHeart from "@/components/icons/heart"
import { Link } from "@/components/ui/link"
import { CollectionCollaborationApi } from "../collection-collaboration-api"
import { relativeCanonicalUrl } from "../entity-utils"
import { showGenericErrorToast } from "../toast-utils"
import { savedPlacesApi } from "./api/saved-places-api"
import { selectIsSplitView } from "./app-slice"
import { selectCurrentCollectionRef } from "./collection-slice"
import { selectCurrInspirationRef } from "./inspiration-slice"

const COLLECTION_NAME_UNIQUE_ERROR =
  "There is already a collection with this name."

const SAVED_TO_COLLECTION_TOAST_ID = "saved-to-collection"

function savedToCollectionLink(collection: Collection) {
  return (
    <Link
      href={relativeCanonicalUrl(collection.canonical_url)}
      className="no-translate font-semibold hover:underline"
      onClick={() => toastManager.close(SAVED_TO_COLLECTION_TOAST_ID)}
    >
      {collection.name}
    </Link>
  )
}

function isNameTakenError(error: unknown): boolean {
  if (!isSerializedClientError(error, "ClientValidationException")) return false
  const details = error.data as
    | Record<string, { _errors: string[] }>
    | undefined
  return details?.name?._errors?.includes("taken") ?? false
}

const savedPlaceApi = new SavedPlaceApi(buildClientAxios())
const collectionCollaborationApi = new CollectionCollaborationApi(
  buildClientAxios()
)

const handleSavedCollectionChange = createAsyncThunk<
  void,
  { place: Place; collection: Collection },
  { dispatch: AppDispatch }
>(
  "savedThunks/handleSavedCollectionChange",
  async ({ place, collection }, { dispatch }) => {
    toastManager.add({
      id: SAVED_TO_COLLECTION_TOAST_ID,
      title: (
        <>
          <span className="no-translate">{place.name}</span> saved to{" "}
          {savedToCollectionLink(collection)}.
        </>
      ),
      timeout: TOAST_WITH_ACTION_DURATION,
      data: {
        icon: {
          icon: IconHeart,
          className: "fill-saved stroke-saved",
        },
      },
      actionProps: {
        children: "Change",
        onClick: () => {
          void (async () => {
            toastManager.close(SAVED_TO_COLLECTION_TOAST_ID)
            await dispatch(
              removePlaceFromCollection({
                place,
                collection,
              })
            )
            void dispatch(openSaveDialog(place))
            sendAnalyticsEvent("CollectionChange")
          })()
        },
      },
    })
  }
)

export const fetchPlaceResults = createAsyncThunk<
  void,
  {
    bbox?: BoundingBox
    page?: number
    force?: boolean
  } | void,
  {
    state: RootState
  }
>(
  "savedThunks/fetchPlaceResults",
  async (params, { getState, dispatch, requestId }) => {
    const isSplitView = selectIsSplitView(getState())
    const { currentView, selectedType, searchString, placeSearchRequestId } =
      getState().savedSlice

    const page = params?.page || 1
    const bbox = params?.bbox

    // If we're in the map tab and no bbox is provided, skip fetching
    // UNLESS an explicit page was requested (for pagination support)
    // or force is true (for view changes that need to re-fetch with different page size).
    // A skipped fetch must not supersede an in-flight map request.
    if (currentView === "map" && !bbox && !params?.page && !params?.force) {
      if (!placeSearchRequestId) dispatch(setLoading(false))
      return
    }

    dispatch(setPlaceSearchRequestId(requestId))
    try {
      dispatch(setLoading(true))

      const result = await savedPlaceApi.type_search(
        selectedType,
        searchString,
        bbox,
        page,
        isSplitView && currentView ? 18 : undefined
      )

      // Filter changes and searches can overlap; only the newest request may update the page.
      if (getState().savedSlice.placeSearchRequestId === requestId) {
        dispatch(setPlaceSearchResults(result))
      }
    } catch (e) {
      console.error("Failed to fetch place search results", e)
    } finally {
      if (getState().savedSlice.placeSearchRequestId === requestId) {
        dispatch(setPlaceSearchRequestId(undefined))
        dispatch(setLoading(false))
      }
    }
  }
)

export const handleTabChange = createAsyncThunk<
  void,
  SavedTab,
  { dispatch: AppDispatch; state: RootState }
>("savedThunks/handleTabChange", async (tab, { dispatch }) => {
  dispatch(setCurrentTab(tab))
  if (tab === "guides") {
    dispatch(setCurrentView(undefined))
    void dispatch(
      savedInspirationsApi.endpoints.getSavedGuides.initiate({ page: 1 })
    )
  } else if (tab === "collections") {
    dispatch(setCurrentView(undefined))
    void dispatch(
      collectionsApi.endpoints.getCollections.initiate({
        page: 1,
        includeFavorites: true,
      })
    )
  } else {
    void dispatch(fetchPlaceResults())
  }
})

export const handleTypeChange = createAsyncThunk<
  void,
  {
    type: SavedType
    bbox?: BoundingBox
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "savedThunks/handleTypeChange",
  async ({ type, bbox }, { dispatch, getState }) => {
    if (type === selectSelectedType(getState())) return

    dispatch(setSelectedType(type))
    void dispatch(fetchPlaceResults({ bbox }))
  }
)

export const handleViewChange = createAsyncThunk<
  void,
  SavedView,
  { dispatch: AppDispatch; state: RootState }
>("savedThunks/handleViewChange", async (value, { dispatch, getState }) => {
  const state = getState()
  const prevView = selectCurrentView(state)
  const isSplitView = selectIsSplitView(state)
  dispatch(setCurrentView(value))

  // When switching TO map in split view, re-fetch with smaller page size (18)
  // When switching FROM map to list on mobile (non-split), preserve bbox results
  // When switching FROM map in split view, re-fetch all (map panel is gone, bbox no longer applies)
  if (value && isSplitView) {
    // Re-fetch with 18 items for split view (force bypasses the "map without bbox" early return)
    await dispatch(fetchPlaceResults({ force: true }))
  } else if (value !== "map") {
    if (prevView === "map" && !isSplitView) {
      // Mobile: preserve bbox results when toggling from map to list
      return
    }
    await dispatch(fetchPlaceResults())
  }
})

export const handleSearchClear = createAsyncThunk<
  void,
  {
    bbox?: BoundingBox
  } | void,
  { dispatch: AppDispatch; state: RootState }
>("savedThunks/handleSearchClear", async (params, { dispatch }) => {
  dispatch(setSearchString(""))
  dispatch(setPlaceSearchResults(undefined))

  await dispatch(fetchPlaceResults({ bbox: params?.bbox }))
})

export const initSaved = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("savedThunks/initSaved", async (_, { dispatch, getState }) => {
  dispatch(setLoading(true))

  sendAnalyticsEvent("ViewSavedPage")

  if (selectCurrentTab(getState()) === "places") {
    await dispatch(fetchPlaceResults())
  }

  if (!getState().savedSlice.placeSearchRequestId) {
    dispatch(setLoading(false))
  }
})

export const fetchMapMarkers = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("savedThunks/fetchMapMarkers", async (_, { dispatch, getState }) => {
  try {
    if (selectMapMarkers(getState())?.length) {
      return
    }

    const result = await savedPlaceApi.fetchMapMarkers()
    dispatch(
      setMapMarkers(
        result.data.map(placePlaceholderToMapMarker).filter(present)
      )
    )
  } catch (e) {
    console.error("Failed to fetch map markers", e)
  }
})

export const openSaveDialog = createAsyncThunk<
  void,
  Place,
  { dispatch: AppDispatch }
>("savedThunks/openSaveDialog", async (place, { dispatch }) => {
  dispatch(setSaveDialogPlace(place))
  dispatch(setSaveDialogOpen(true))
})

export const openSaveDialogForPlaceRefs = createAsyncThunk<
  void,
  PlaceRef[],
  { dispatch: AppDispatch }
>("savedThunks/openSaveDialogForPlaceRefs", async (placeRefs, { dispatch }) => {
  dispatch(setSaveDialogPlaceRefs(placeRefs))
  dispatch(setSaveDialogOpen(true))
})

// Completes the save an anonymous user was gated out of before signing up.
export const runPendingSaveAction = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("savedThunks/runPendingSaveAction", async (_, { dispatch }) => {
  const pending = getPendingSave()
  if (!pending) return

  clearPendingSave()

  if (pending.type === "place") {
    const minimalPlace = {
      entity_ref: pending.entityRef,
      name: pending.title,
    } as Place
    void dispatch(openSaveDialog(minimalPlace))
    return
  }

  if (pending.type === "collection") {
    try {
      await dispatch(
        collectionsApi.endpoints.addFavoriteCollection.initiate({
          ref: pending.collectionRef,
        })
      ).unwrap()
      toastManager.add({
        description: `${pending.title} saved`,
        id: "add-favorite-collection",
        data: {
          icon: { icon: IconHeart, className: "fill-saved stroke-saved" },
        },
      })
    } catch {
      // silent fail
    }
    return
  }

  try {
    await dispatch(
      savedInspirationsApi.endpoints.addSavedGuide.initiate({
        entity_ref: pending.entityRef,
      } as Inspiration)
    ).unwrap()

    toastManager.add({
      description: `${pending.title} saved`,
      id: "add-saved-inspiration",
      data: {
        icon: {
          icon: IconHeart,
          className: "fill-saved stroke-saved",
        },
      },
    })
  } catch {
    // silent fail
  }
})

export const addPlacesToCollection = createAsyncThunk<
  void,
  {
    entityRefs: PlaceRef[]
    collection: Collection
    tripRef?: TripRef
  },
  { dispatch: AppDispatch }
>(
  "savedThunks/addPlacesToCollection",
  async ({ entityRefs, collection, tripRef }, { dispatch }) => {
    try {
      await dispatch(
        savedPlacesApi.endpoints.bulkAddSavedPlaces.initiate({
          entityRefs,
          collectionRef: collection.ref,
          tripRef,
        })
      ).unwrap()

      dispatch(setLastUsedCollection(collection))
      dispatch(userSlice.actions.patchUser({ has_saved_places: true }))
      sendAnalyticsEvent("CollectionBulkAddPlaces")

      toastManager.add({
        id: SAVED_TO_COLLECTION_TOAST_ID,
        title: (
          <>
            {pluralize("place", entityRefs.length, true)} saved to{" "}
            {savedToCollectionLink(collection)}.
          </>
        ),
        timeout: TOAST_WITH_ACTION_DURATION,
        data: {
          icon: {
            icon: IconHeart,
            className: "fill-saved stroke-saved",
          },
        },
      })
    } catch (error) {
      console.error("Failed to save places to collection", error)
      showGenericErrorToast()
    }
  }
)

const handleAddPlaceToCollection = createAsyncThunk<
  void,
  {
    place: Place
    collection: Collection
    tripRef?: TripRef
    showToast?: boolean
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "savedThunks/handleAddPlaceToCollection",
  async (
    { place, collection, tripRef, showToast = true },
    { dispatch, getState }
  ) => {
    const currInspirationRef = selectCurrInspirationRef(getState())
    const currCollectionRef = selectCurrentCollectionRef(getState())

    await dispatch(
      savedPlacesApi.endpoints.addSavedPlace.initiate({
        entity: place,
        tripRef,
        collectionRef: collection.ref,
        commissionSourceRef: currInspirationRef || currCollectionRef,
      })
    ).unwrap()

    dispatch(setLastUsedCollection(collection))
    dispatch(userSlice.actions.patchUser({ has_saved_places: true }))

    if (showToast) {
      void dispatch(handleSavedCollectionChange({ place, collection }))
    }
  }
)

export const createNewCollection = createAsyncThunk<
  Collection | undefined,
  { name: string; isPrivate: boolean; place?: Place },
  { dispatch: AppDispatch; state: RootState }
>(
  "savedThunks/createNewCollection",
  async (
    { name, isPrivate, place },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const collection = await dispatch(
        collectionsApi.endpoints.createCollection.initiate({
          name,
          isPrivate,
        })
      ).unwrap()

      const trigger = selectCollectionTrigger(getState())
      sendAnalyticsEvent({
        name: "CollectionCreate",
        properties: {
          trigger,
          public: !isPrivate,
        },
      })

      if (place) {
        await dispatch(handleAddPlaceToCollection({ place, collection }))
      }

      return collection
    } catch (error) {
      if (isNameTakenError(error)) {
        return rejectWithValue(COLLECTION_NAME_UNIQUE_ERROR)
      } else {
        console.error("Failed to create collection", error)
        showGenericErrorToast()
      }
    }
  }
)

export const addPlaceToCollection = createAsyncThunk<
  void,
  {
    place: Place
    tripRef?: TripRef
    collection: Collection
    showToast?: boolean
  },
  { dispatch: AppDispatch }
>(
  "savedThunks/addPlaceToCollection",
  async ({ place, tripRef, collection, showToast = true }, { dispatch }) => {
    try {
      await dispatch(
        handleAddPlaceToCollection({
          place,
          collection,
          tripRef,
          showToast,
        })
      )
      sendAnalyticsEvent("CollectionAddPlace")
    } catch (error) {
      console.error("Failed to save to collection", error)
      showGenericErrorToast()
    }
  }
)

export const removePlaceFromCollection = createAsyncThunk<
  void,
  { place: Place; collection: Collection },
  { dispatch: AppDispatch }
>(
  "savedThunks/removePlaceFromCollection",
  async ({ place, collection }, { dispatch }) => {
    try {
      await dispatch(
        savedPlacesApi.endpoints.removeSavedPlace.initiate({
          entityRef: place.entity_ref,
          collectionRef: collection.ref,
        })
      ).unwrap()

      sendAnalyticsEvent("CollectionRemovePlace")
    } catch (error) {
      console.error("Failed to remove place from collection", error)
      showGenericErrorToast()
    }
  }
)

export const updateCollection = createAsyncThunk<
  void,
  { collectionRef: CollectionRef; name: string; isPrivate: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  "savedThunks/updateCollection",
  async (
    { collectionRef, name, isPrivate },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const updatedCollection = await dispatch(
        collectionsApi.endpoints.updateCollection.initiate({
          ref: collectionRef,
          name,
          isPrivate,
        })
      ).unwrap()

      const currentEditingCollection = selectEditingCollection(getState())
      if (currentEditingCollection) {
        const updatedEditingCollection = {
          ...currentEditingCollection,
          name: updatedCollection.name,
          public: updatedCollection.public,
        }
        dispatch(
          savedSlice.actions.setEditingCollection(updatedEditingCollection)
        )
      }

      toastManager.add({ type: "success", description: "Collection updated." })
    } catch (error) {
      if (isNameTakenError(error)) {
        return rejectWithValue(COLLECTION_NAME_UNIQUE_ERROR)
      } else {
        console.error("Failed to update collection", error)
        showGenericErrorToast()
      }
    }
  }
)

export const deleteCollection = createAsyncThunk<
  void,
  Collection,
  { dispatch: AppDispatch }
>("savedThunks/deleteCollection", async (collection, { dispatch }) => {
  try {
    await dispatch(
      collectionsApi.endpoints.deleteCollection.initiate(collection.ref)
    ).unwrap()

    // Deleting a collection also deletes the saves that were only in it
    // (SavedCollection#deactivate), so drop the ref and any place left with
    // no collection rather than refetching the whole list.
    dispatch(
      savedPlacesApi.util.updateQueryData(
        "getSavedPlacesList",
        undefined,
        (draft) =>
          draft.flatMap((savedPlace) => {
            if (!savedPlace.collections?.includes(collection.ref)) {
              return [savedPlace]
            }
            const collections = savedPlace.collections.filter(
              (ref) => ref !== collection.ref
            )
            return collections.length > 0
              ? [{ ...savedPlace, collections }]
              : []
          })
      )
    )
    dispatch(savedPlacesApi.util.invalidateTags(["SavedPlaces"]))
    toastManager.add({ type: "success", description: "Collection removed." })
  } catch (error) {
    console.error("Failed to remove collection", error)
    showGenericErrorToast()
  }
})

export const openCollectionDialog = createAsyncThunk<
  void,
  { editCollection?: Collection; trigger?: CollectionTrigger } | void,
  { dispatch: AppDispatch; state: RootState }
>("savedThunks/openCollectionDialog", async (params, { dispatch }) => {
  const editCollection = params?.editCollection
  const trigger = params?.trigger

  // Initialize the form state based on the editing collection if present
  if (editCollection) {
    dispatch(setCollectionDialogName(editCollection.name))
    dispatch(setCollectionDialogIsPrivate(!editCollection.public))
    dispatch(setEditingCollection(editCollection))
  } else {
    dispatch(setCollectionDialogName(""))
    dispatch(setCollectionDialogIsPrivate(false))
    dispatch(setEditingCollection(undefined))
  }

  // Reset error state
  dispatch(setCollectionDialogError(""))
  dispatch(setCollectionDialogIsLoading(false))

  // Open the dialog with trigger info if provided
  if (trigger) {
    dispatch(setCollectionDialogOpen({ open: true, trigger }))
  } else {
    dispatch(setCollectionDialogOpen(true))
  }
})

export const closeCollectionDialog = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("savedThunks/closeCollectionDialog", async (_, { dispatch }) => {
  dispatch(setCollectionDialogOpen(false))
  dispatch(setCollectionDialogName(""))
  dispatch(setCollectionDialogIsPrivate(false))
  dispatch(setCollectionDialogError(""))
  dispatch(setCollectionDialogIsLoading(false))
  dispatch(setEditingCollection(undefined))
})

export const handleCollectionFormSubmit = createAsyncThunk<
  void,
  (collection?: Collection) => void,
  { dispatch: AppDispatch; state: RootState }
>(
  "savedThunks/handleCollectionFormSubmit",
  async (onSuccess, { dispatch, getState }) => {
    if (selectCollectionDialogIsLoading(getState())) return

    const name = selectCollectionDialogName(getState())
    const isPrivate = selectCollectionDialogIsPrivate(getState())
    const editingCollection = selectEditingCollection(getState())
    const place = selectSaveDialogPlace(getState())

    const trimmedName = name.trim()
    let hadError = false
    let createdCollection: Collection | undefined

    dispatch(setCollectionDialogIsLoading(true))
    try {
      if (editingCollection) {
        await dispatch(
          updateCollection({
            collectionRef: editingCollection.ref,
            name: trimmedName,
            isPrivate,
          })
        ).unwrap()
      } else {
        createdCollection = await dispatch(
          createNewCollection({
            name: trimmedName,
            isPrivate,
            place,
          })
        ).unwrap()
      }
    } catch (rejectedValue) {
      if (typeof rejectedValue === "string") {
        dispatch(setCollectionDialogError(rejectedValue))
        hadError = true
      }
    } finally {
      dispatch(setCollectionDialogIsLoading(false))
      if (!hadError) {
        onSuccess(createdCollection)
      }
    }
  }
)

export const leaveCollection = createAsyncThunk<
  void,
  Collection,
  { dispatch: AppDispatch; state: RootState }
>("savedThunks/leaveCollection", async (collection, { dispatch, getState }) => {
  try {
    const userRef = selectCurrentUserRef(getState())

    await collectionCollaborationApi.removeCollaborator(collection.ref, userRef)

    // Invalidate collections cache so it refetches without this collection
    dispatch(collectionsApi.util.invalidateTags(["Collections"]))

    toastManager.add({
      type: "success",
      description: (
        <>
          You have left “<span className="no-translate">{collection.name}</span>
          ”.
        </>
      ),
    })
    sendAnalyticsEvent("CollectionLeave")
  } catch (error) {
    console.error("Failed to leave collection", error)
    showGenericErrorToast()
  }
})

export interface UpdatePlaceNotePayload {
  placeRef: PlaceRef
  collectionRef: CollectionRef
  note?: string
  updatedSavedPlace: FavoritePlace
  userRef: UserRef
}

export const updatePlaceNote = createAsyncThunk<
  UpdatePlaceNotePayload,
  {
    placeRef: PlaceRef
    collectionRef: CollectionRef
    note?: string
  },
  { state: RootState }
>(
  "savedThunks/updatePlaceNote",
  async ({ placeRef, collectionRef, note }, { getState, rejectWithValue }) => {
    try {
      const updatedSavedPlace = await savedPlaceApi.updateNote(
        placeRef,
        collectionRef,
        note
      )

      return {
        placeRef,
        collectionRef,
        note,
        updatedSavedPlace,
        userRef: selectCurrentUserRef(getState()),
      }
    } catch (error) {
      console.error("Failed to update note", error)
      showGenericErrorToast()
      return rejectWithValue(error)
    }
  }
)

export interface DeleteSourceSummaryPayload {
  placeRef: PlaceRef
  collectionRef: CollectionRef
  updatedSavedPlace: FavoritePlace
  userRef: UserRef
}

export const deleteSourceSummary = createAsyncThunk<
  DeleteSourceSummaryPayload,
  {
    placeRef: PlaceRef
    collectionRef: CollectionRef
  },
  { state: RootState }
>(
  "savedThunks/deleteSourceSummary",
  async ({ placeRef, collectionRef }, { getState, rejectWithValue }) => {
    try {
      const updatedSavedPlace = await savedPlaceApi.deleteSourceSummary(
        placeRef,
        collectionRef
      )

      return {
        placeRef,
        collectionRef,
        updatedSavedPlace,
        userRef: selectCurrentUserRef(getState()),
      }
    } catch (error) {
      console.error("Failed to delete source summary", error)
      showGenericErrorToast()
      return rejectWithValue(error)
    }
  }
)

export const reloadSavedPlacesList = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("savedThunks/reloadSavedPlacesList", async (_, { dispatch }) => {
  dispatch(
    savedPlacesApi.util.invalidateTags(["SavedPlaces", "SavedPlaceRefs"])
  )
  void dispatch(
    savedPlacesApi.endpoints.getSavedPlacesList.initiate(undefined, {
      forceRefetch: true,
    })
  )
})

export const openImportCollectionDialog = createAsyncThunk<
  void,
  CollectionImportType,
  { dispatch: AppDispatch }
>("savedThunks/importCollection", async (type, { dispatch }) => {
  if (type === "link") {
    dispatch(savedSlice.actions.setImportTravelResourceDialogOpen(true))
  } else if (type === "google") {
    dispatch(savedSlice.actions.setImportGooglePinsDialogOpen(true))
  }
  sendAnalyticsEvent({
    name: "ImportCollectionClick",
    properties: { type },
  })
})

export const createCollectionFromLink = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch; state: RootState }
>(
  "savedThunks/createCollectionFromLink",
  async (link, { dispatch, getState }) => {
    const collectionRef = selectCurrentCollectionRef(getState())
    await dispatch(
      collectionsApi.endpoints.createFromLink.initiate({
        url: link,
        collectionRef,
        isPublic: false,
      })
    ).unwrap()
    dispatch(setPendingCollectionImport(collectionRef ? collectionRef : true))
    sendAnalyticsEvent({
      name: "CollectionCreate",
      properties: { trigger: "import", importUrl: link, public: false },
    })
  }
)
